/**
 * Formato de costos del AI Gateway.
 *
 * Adaptativo: montos menores a $0.01 se muestran con 4 decimales para que una
 * pregunta de 1¢ o fracciones no se vean como "$0.00" (rendición de cuentas).
 */
export function formatUsdCents(cents: number): string {
  const usd = cents / 100;
  if (usd !== 0 && Math.abs(usd) < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

/** Alias semántico para costos chicos (requests individuales). */
export const formatCostUsd = formatUsdCents;

/**
 * Formato de montos "grandes" (saldos, topes): dólares redondos sin
 * decimales ($5), con decimales si hay centavos ($5.50).
 */
export function formatUsd(cents: number): string {
  const usd = cents / 100;
  return usd % 1 === 0 ? `$${usd.toFixed(0)}` : `$${usd.toFixed(2)}`;
}

/** Créditos = centavos de USD (1 crédito = 1¢). Se muestra como entero. */
export function formatCredits(cents: number): string {
  return Math.round(cents).toLocaleString();
}

/**
 * Formato corto de créditos para tablas y celdas de consumo: entero con
 * separador de miles si ≥ 10 (250 → "250", 5000 → "5,000"), 1 decimal si es
 * menor (0.2 → "0.2"). La unidad ("créditos") se indica una vez por bloque,
 * no en cada número.
 */
export function fmtCredits(credits: number): string {
  if (credits !== 0 && Math.abs(credits) < 10) {
    return credits.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return Math.round(credits).toLocaleString('en-US');
}

/** Umbral por defecto de "saldo bajo" ($5 USD). */
export const DEFAULT_LOW_BALANCE_CENTS = 500;

const LOW_BALANCE_LS_KEY = 'deployalo.aiGateway.lowBalanceCents';

export function getStoredLowBalanceCents(): number {
  try {
    const raw = localStorage.getItem(LOW_BALANCE_LS_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_LOW_BALANCE_CENTS;
  } catch {
    return DEFAULT_LOW_BALANCE_CENTS;
  }
}

export function storeLowBalanceCents(cents: number): void {
  try {
    localStorage.setItem(LOW_BALANCE_LS_KEY, String(cents));
  } catch { /* localStorage no disponible */ }
}

/** Color de la barra de progreso de un tope según % consumido. */
export function capBarColor(pct: number): string {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-brand';
}

/** Color del texto "usado de tope" según % consumido. */
export function capTextColor(pct: number): string {
  if (pct >= 100) return 'text-red-400';
  if (pct >= 80) return 'text-amber-400';
  return 'text-muted-foreground';
}

/** Escapa un valor para CSV (RFC 4180 básico). */
export function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

/** Descarga un CSV generado en cliente. */
export function downloadCsv(filename: string, headers: string[], rows: unknown[][]): void {
  const csv = [headers.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
